﻿import * as THREE from "three/webgpu";
import {AbstractPointSnapStrategy} from './AbstractPointSnapStrategy';
import {PointSnapStrategyType} from './PointSnapStrategyType';
import type {PointSnapRequest, PointSnapResult} from './PointSnapTypes';
import {
    LineMaterialFactory,
    isScreenSpaceDashedLineNodeMaterial,
    syncScreenSpaceDashedLineGeometry,
} from '../../material';
import type { SketchPointObject } from '../../mesh/sketch';
import type { ReferencePlane } from '../../reference';
import type { ModelPointAxisCandidate, PointHorizontalVerticalCandidateInfo, PointHorizontalVerticalTrackingLineType } from '../../../types/sketch/ModelPointAxisTracking';
import {QuickSnapMode} from './QuickSnapMode';
import {GeometryInteractionState} from '../interactionState/GeometryInteractionState';
import {scheduleDisposableResourceDisposal} from '../../utils';

/** 与点水平/竖直追踪线方向常量。 */
const PointHorizontalVerticalTrackingLineType = {
    /** 水平追踪线。 */
    Horizontal: 'horizontal',
    /** 竖直追踪线。 */
    Vertical: 'vertical',
} as const;



/**
 * 与点水平/竖直捕捉策略。
 * 职责：
 * 1. 普通模式：遍历活跃草图组中所有可见草图点与绘制态临时参考点；
 *    检测鼠标与各点的水平/竖直对齐关系；
 *    构建追踪引导虚线并加入场景；
 *    保存对齐状态和吸附交叉点供 SketchDrawingManager 查询（始终返回 null）。
 * 2. 快速 H/V 捕捉模式（QuickSnapMode.HorizontalVertical）：
 *    全局找最近草图点作为参考，高亮该点；
 *    将鼠标约束到参考点的水平或竖直轴（取更近的那条）；
 *    返回约束点（权重 200）。
 */
export class PointHorizontalVerticalPointSnapStrategy extends AbstractPointSnapStrategy {
    /** 策略类型。 */
    public readonly strategyType: PointSnapStrategyType = PointSnapStrategyType.PointHorizontalVertical;

    /**
     * 普通模式与点水平/竖直捕捉像素阈值。
     * 说明：
     * 1. 该策略仅用于补充点类命中之外的水平/竖直追踪语义；
     * 2. 阈值需比普通点捕捉更收敛，避免鼠标尚未明显贴近轴线时就过早进入 H/V 追踪。
     */
    protected override readonly snapPixelThreshold: number = 6;

    /** 快速捕捉权重，确保必定胜出所有普通策略。 */
    private static readonly QUICK_SNAP_WEIGHT = 200;

    
    /**
     * 普通模式与点水平/竖直追踪线缓存。
     *
     * 说明：
     * 1. 参考圆绘制预览的生命周期，追踪线首次命中时创建；
     * 2. 后续鼠标移动只原地更新几何，避免每帧销毁/创建；
     * 3. 未命中的方向仅隐藏，绘制结束或策略清理时才释放。
     */
    private readonly pointHvTrackingLines: Record<PointHorizontalVerticalTrackingLineType, THREE.Line | null> = {
        [PointHorizontalVerticalTrackingLineType.Horizontal]: null,
        [PointHorizontalVerticalTrackingLineType.Vertical]: null,
    };
    

    /** 当前帧是否检测到水平对齐。 */
    private hasHorizontal: boolean = false;
    /** 当前帧是否检测到竖直对齐。 */
    private hasVertical: boolean = false;
    /** 当前帧与点水平/竖直吸附投影点（世界坐标）。 */
    private snapPoint: THREE.Vector3 | null = null;
    /**
     * 水平追踪线对应的参考点局部 Y 坐标。
     * 说明：与 HV 方向捕捉交叉时，用此值在参考平面中计算实际交点。
     */
    private horzRefLocalY: number | null = null;
    /**
     * 竖直追踪线对应的参考点局部 X 坐标。
     * 说明：与 HV 方向捕捉交叉时，用此值在参考平面中计算实际交点。
     */
    private vertRefLocalX: number | null = null;

    /** 两个方向的模型候选排序信息，避免指针热路径反复分配向量和记录。 */
    private readonly modelAxisCandidateInfos: readonly PointHorizontalVerticalCandidateInfo[] = Array.from(
        { length: 2 },
        () => ({ worldPos: new THREE.Vector3(), localPos: new THREE.Vector2(), screenAbsDx: 0, screenAbsDy: 0, modelReference: null }),
    );

    
    /** 当前快速捕捉高亮的草图点。 */
    private quickSnapHighlightedPoint: SketchPointObject | null = null;
    /** 当前高亮点的原始可见性（用于恢复）。 */
    private quickSnapOriginalVisibility: boolean | null = null;
    /** 快速捕捉追踪线（水平）。 */
    private quickSnapHorzLine: THREE.Line | null = null;
    /** 快速捕捉追踪线（竖直）。 */
    private quickSnapVertLine: THREE.Line | null = null;
    

    /**
     * 执行与点水平/竖直捕捉。
     * @param request 点捕捉请求参数。
     * @returns 快速模式下返回约束点；普通模式始终返回 null。
     */
    public resolve(request: PointSnapRequest): PointSnapResult | null {
        // 上帧源模型引用只在本帧真正胜出时重新写入，避免缓存失效后仍持有旧记录。
        for (const info of this.modelAxisCandidateInfos) info.modelReference = null;
        
        if (request.quickSnapMode === QuickSnapMode.HorizontalVertical) {
            this.viewer?.sketchSessionReferencePointManager?.clearModelPointAxisTracking();
            return this.resolveQuickSnap(request);
        }
        

        // 普通模式：清除快速捕捉残留。
        this.clearQuickSnapState();

        // 重置状态，但保留普通模式追踪线对象，后续只更新/隐藏，避免高频销毁重建。
        this.snapPoint = null;
        this.hasHorizontal = false;
        this.hasVertical = false;
        this.horzRefLocalY = null;
        this.vertRefLocalX = null;

        // 获取参考平面。
        const referencePlane = this.viewer?.currentReferencePlane ?? null;
        if (!referencePlane) {
            this.hideInactivePointHvTrackingLines(false, false);
            this.viewer?.sketchSessionReferencePointManager?.clearModelPointAxisTracking();
            return null;
        }

        const rawPoint = request.worldPosition;
        const allPoints = this.collectCandidatePoints(request);

        const mouseLocal = referencePlane.worldToLocal(rawPoint);
        // 收集水平/竖直对齐候选点，循环结束后统一选择屏幕轴向偏差最小的点。
        const horzCandidateInfos: Array<PointHorizontalVerticalCandidateInfo> = [];
        const vertCandidateInfos: Array<PointHorizontalVerticalCandidateInfo> = [];

        for (const point of allPoints) {
            // 跳过不可见点。
            if (!point.visible || point.isDrawingPreviewPoint) {
                continue;
            }

            const worldPos = point.getPosition();
            const localPos = referencePlane.worldToLocal(worldPos);

            const screenPosition = this.projectWorldToScreen(worldPos);
            if (screenPosition === null) {
                continue;
            }

            const screenDx = request.screenPosition.x - screenPosition.x;
            const screenDy = request.screenPosition.y - screenPosition.y;
            const screenAbsDx = Math.abs(screenDx);
            const screenAbsDy = Math.abs(screenDy);

            const candidateInfo: PointHorizontalVerticalCandidateInfo = {
                modelReference: null,
                worldPos,
                localPos,
                screenAbsDx,
                screenAbsDy,
            };

            // 水平候选以屏幕空间 y 偏差为准；处于同一水平线的点应同时进入候选。
            if (screenAbsDy <= this.snapPixelThreshold) {
                horzCandidateInfos.push(candidateInfo);
            }

            // 竖直候选以屏幕空间 x 偏差为准；处于同一直线的点应同时进入候选。
            if (screenAbsDx <= this.snapPixelThreshold) {
                vertCandidateInfos.push(candidateInfo);
            }
        }

        let closestHorzInfo = horzCandidateInfos.reduce<PointHorizontalVerticalCandidateInfo | null>((closestInfo, candidateInfo) => {
            if (closestInfo === null) {
                return candidateInfo;
            }

            if (candidateInfo.screenAbsDy < closestInfo.screenAbsDy) {
                return candidateInfo;
            }

            if (
                candidateInfo.screenAbsDy === closestInfo.screenAbsDy
                && candidateInfo.screenAbsDx < closestInfo.screenAbsDx
            ) {
                return candidateInfo;
            }

            return closestInfo;
        }, null);

        let closestVertInfo = vertCandidateInfos.reduce<PointHorizontalVerticalCandidateInfo | null>((closestInfo, candidateInfo) => {
            if (closestInfo === null) {
                return candidateInfo;
            }

            if (candidateInfo.screenAbsDx < closestInfo.screenAbsDx) {
                return candidateInfo;
            }

            if (
                candidateInfo.screenAbsDx === closestInfo.screenAbsDx
                && candidateInfo.screenAbsDy < closestInfo.screenAbsDy
            ) {
                return candidateInfo;
            }

            return closestInfo;
        }, null);

        // 直接查询完整可见投影点索引；不依赖已显示的临时点，也不改变普通端点捕捉候选。
        const referencePointManager = this.viewer?.sketchSessionReferencePointManager;
        const modelCandidates = referencePointManager?.queryModelPointAxisCandidates(
            request.screenPosition, this.snapPixelThreshold, request.excludedTargetObjectIds,
        );
        closestHorzInfo = this.selectModelAxisCandidate(closestHorzInfo, modelCandidates?.horizontal ?? null, request, referencePlane, 'horizontal');
        closestVertInfo = this.selectModelAxisCandidate(closestVertInfo, modelCandidates?.vertical ?? null, request, referencePlane, 'vertical');
        referencePointManager?.showModelPointAxisTracking(closestHorzInfo?.modelReference ?? null, closestVertInfo?.modelReference ?? null);

        // 构建水平追踪线并记录状态。
        if (closestHorzInfo) {
            this.hasHorizontal = true;
            this.horzRefLocalY = closestHorzInfo.localPos.y;
            // 终点：鼠标 x 保持，y 固定为参考点 y（鼠标在水平轴上的投影点）。
            const endWorld = referencePlane.localToWorld(new THREE.Vector2(mouseLocal.x, closestHorzInfo.localPos.y));
            this.updatePointHvTrackingLine(
                PointHorizontalVerticalTrackingLineType.Horizontal,
                closestHorzInfo.worldPos,
                endWorld,
            );
        }

        // 构建竖直追踪线并记录状态。
        if (closestVertInfo) {
            this.hasVertical = true;
            this.vertRefLocalX = closestVertInfo.localPos.x;
            // 终点：x 固定为参考点 x，y 跟随鼠标（鼠标在竖直轴上的投影点）。
            const endWorld = referencePlane.localToWorld(new THREE.Vector2(closestVertInfo.localPos.x, mouseLocal.y));
            this.updatePointHvTrackingLine(
                PointHorizontalVerticalTrackingLineType.Vertical,
                closestVertInfo.worldPos,
                endWorld,
            );
        }

        this.hideInactivePointHvTrackingLines(Boolean(closestHorzInfo), Boolean(closestVertInfo));

        // 计算最终吸附投影点。
        if (closestHorzInfo && closestVertInfo) {
            // 同时有水平和竖直对齐：交叉点（x 取竖直参考点，y 取水平参考点）。
            this.snapPoint = referencePlane.localToWorld(
                new THREE.Vector2(closestVertInfo.localPos.x, closestHorzInfo.localPos.y),
            );
        } else if (closestHorzInfo) {
            // 仅水平：x 跟随鼠标，y 固定为水平参考点的 y。
            this.snapPoint = referencePlane.localToWorld(
                new THREE.Vector2(mouseLocal.x, closestHorzInfo.localPos.y),
            );
        } else if (closestVertInfo) {
            // 仅竖直：x 固定为竖直参考点的 x，y 跟随鼠标。
            this.snapPoint = referencePlane.localToWorld(
                new THREE.Vector2(closestVertInfo.localPos.x, mouseLocal.y),
            );
        }

        // 始终返回 null，不影响普通捕捉。
        return null;
    }

    /**
     * 获取当前帧的与点水平/竖直对齐状态。
     * 说明：应在 resolve() 调用后查询，获取最新状态。
     * @returns 当前对齐状态，包含对齐标志、交叉吸附点和轴坐标（供外部计算与 HV 方向捕捉的交叉）。
     */
    public getActiveState(): {
        hasHorizontal: boolean;
        hasVertical: boolean;
        snapPoint: THREE.Vector3 | null;
        /** 水平追踪线的参考点局部 Y 坐标；hasHorizontal 为 true 时有效。 */
        horzRefLocalY: number | null;
        /** 竖直追踪线的参考点局部 X 坐标；hasVertical 为 true 时有效。 */
        vertRefLocalX: number | null;
    } {
        return {
            hasHorizontal: this.hasHorizontal,
            hasVertical: this.hasVertical,
            snapPoint: this.snapPoint,
            horzRefLocalY: this.horzRefLocalY,
            vertRefLocalX: this.vertRefLocalX,
        };
    }

    /**
     * 覆盖 setEnabled：禁用时主动清除状态，防止残留值被查询方误读。
     * @param enabled 是否启用。
     */
    public override setEnabled(enabled: boolean): void {
        super.setEnabled(enabled);
        if (!enabled) {
            this.snapPoint = null;
            this.hasHorizontal = false;
            this.hasVertical = false;
            this.horzRefLocalY = null;
            this.vertRefLocalX = null;
            this.clearPreview();
        }
    }

    /**
     * 清除所有与点水平/竖直追踪虚线，释放几何体资源。
     * 说明：由 PointSnapManager.clearAllPreviews() 在绘制结束、工具切换或点类捕捉冻结时调用。
     */
    public override clearPreview(): void {
        for (const info of this.modelAxisCandidateInfos) info.modelReference = null;
        this.viewer?.sketchSessionReferencePointManager?.clearModelPointAxisTracking();
        this.snapPoint = null;
        this.hasHorizontal = false;
        this.hasVertical = false;
        this.horzRefLocalY = null;
        this.vertRefLocalX = null;

        // 清除普通模式追踪线。
        this.disposePointHvTrackingLines();
        // 清除快速捕捉状态。
        this.clearQuickSnapState();
    }

    

    /**
     * 快速 H/V 捕捉：全局找最近草图点作为参考，将鼠标约束到水平/竖直轴（取更近的那条）。
     * 说明：
     * - 无距离限制，全局扫描所有可见草图点；
     * - 高亮最近参考点；
     * - 取 |dy| 与 |dx| 的大小决定水平或竖直约束；
     * - 显示从参考点到约束点的追踪虚线；
     * - 返回约束点（权重 200）。
     * @param request 点捕捉请求参数。
     * @returns 约束到水平/竖直轴上的捕捉结果；无参考点时返回 null。
     */
    private resolveQuickSnap(request: PointSnapRequest): PointSnapResult | null {
        const referencePlane = this.viewer?.currentReferencePlane ?? null;
        if (!referencePlane) {
            this.clearQuickSnapState();
            return null;
        }

        const mouseLocal = referencePlane.worldToLocal(request.worldPosition);
        const allPoints = this.collectCandidatePoints(request);
        if (allPoints.length <= 0) {
            this.clearQuickSnapState();
            return null;
        }

        // 全局找屏幕（局部坐标）最近的可见草图点（无距离限制）。
        let bestPoint: SketchPointObject | null = null;
        let bestDist = Infinity;

        for (const point of allPoints) {
            if (!point.visible || point.isDrawingPreviewPoint) continue;
            const localPos = referencePlane.worldToLocal(point.getPosition());
            const dx = mouseLocal.x - localPos.x;
            const dy = mouseLocal.y - localPos.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 1e-4) continue; // 防止自吸附噪声。
            if (dist < bestDist) {
                bestDist = dist;
                bestPoint = point;
            }
        }

        if (!bestPoint) {
            this.clearQuickSnapState();
            return null;
        }

        // 差量高亮参考点。
        if (this.quickSnapHighlightedPoint !== bestPoint) {
            this.clearQuickSnapHighlight();
            this.quickSnapOriginalVisibility = bestPoint.visible;
        }
        // 每帧都重新声明当前点处于外部高亮态，直到显式清除。
        this.applyHoverState(bestPoint);
        this.quickSnapHighlightedPoint = bestPoint;

        const refLocal = referencePlane.worldToLocal(bestPoint.getPosition());
        const refWorld = bestPoint.getPosition();

        const dx = mouseLocal.x - refLocal.x;
        const dy = mouseLocal.y - refLocal.y;

        let snapLocal: THREE.Vector2;
        let isHorizontal: boolean;

        if (Math.abs(dy) <= Math.abs(dx)) {
            // 水平约束：y 固定为参考点 y，x 跟随鼠标。
            snapLocal = new THREE.Vector2(mouseLocal.x, refLocal.y);
            isHorizontal = true;
        } else {
            // 竖直约束：x 固定为参考点 x，y 跟随鼠标。
            snapLocal = new THREE.Vector2(refLocal.x, mouseLocal.y);
            isHorizontal = false;
        }

        const snapWorld = referencePlane.localToWorld(snapLocal);

        // 更新追踪虚线（参考点 → 约束点）。
        this.updateQuickSnapTrackingLine(refWorld, snapWorld, isHorizontal);

        return {
            strategyType: this.strategyType,
            position: snapWorld,
            indicatorPosition: snapWorld,
            targetObject: bestPoint,
            weight: PointHorizontalVerticalPointSnapStrategy.QUICK_SNAP_WEIGHT,
            showIndicator: true,
            indicatorIconOverride: isHorizontal
                ? '/textures/sketch/snap-horizontal.svg'
                : '/textures/sketch/snap-vertical.svg',
        };
    }

    /**
     * 更新快速捕捉追踪虚线（复用几何体，减少 GC）。
     * @param start 追踪线起点（参考点世界坐标）。
     * @param end 原始追踪线终点（约束点世界坐标，显示时会被限制到统一长度）。
     * @param isHorizontal 是否为水平轴追踪线（决定更新哪条线并清除另一条）。
     */
    private updateQuickSnapTrackingLine(start: THREE.Vector3, end: THREE.Vector3, isHorizontal: boolean): void {
        const guideEnd = this.resolveLimitedPointSnapGuideLineEndPoint(start, end);
        // 切换方向时清除另一条线。
        if (isHorizontal && this.quickSnapVertLine) {
            this.disposePointHvTrackingLine(this.quickSnapVertLine);
            this.quickSnapVertLine = null;
        }
        if (!isHorizontal && this.quickSnapHorzLine) {
            this.disposePointHvTrackingLine(this.quickSnapHorzLine);
            this.quickSnapHorzLine = null;
        }

        const targetLineRef = isHorizontal ? this.quickSnapHorzLine : this.quickSnapVertLine;

        if (targetLineRef) {
            // 复用几何体直接更新顶点，并同步屏幕空间虚线数据。
            this.updatePointHvTrackingLineGeometry(targetLineRef, [start, guideEnd]);
        } else {
            // 首次创建。
            const line = this.createPointHvTrackingLine(start, guideEnd);
            this.addPreviewObject(line);
            if (isHorizontal) {
                this.quickSnapHorzLine = line;
            } else {
                this.quickSnapVertLine = line;
            }
        }
    }

    /**
     * 通过交互状态管理器对参考点施加 Hover 高亮。
     * @param point 需要高亮的草图点对象。
     */
    private applyHoverState(point: SketchPointObject): void {
        point.visible = true;
        this.viewer?.interactionStateManager?.setSketchObjectState(
            point,
            GeometryInteractionState.Hover,
        );
    }

    /**
     * 清除快速捕捉高亮点，恢复原始材质。
     */
    private clearQuickSnapHighlight(): void {
        if (this.quickSnapHighlightedPoint !== null) {
            this.viewer?.interactionStateManager?.restoreSketchObjectState(this.quickSnapHighlightedPoint);
            const isTopologyVisibilityManaged = this.quickSnapHighlightedPoint.isEndPoint
                || this.quickSnapHighlightedPoint.isMidPoint
                || this.quickSnapHighlightedPoint.isQuadrantPoint;
            this.quickSnapHighlightedPoint.refreshDefaultVisibilityByTopologyRole();
            if (this.quickSnapOriginalVisibility !== null && !isTopologyVisibilityManaged) {
                this.quickSnapHighlightedPoint.visible = this.quickSnapOriginalVisibility;
            }
        }
        this.quickSnapHighlightedPoint = null;
        this.quickSnapOriginalVisibility = null;
    }

    /**
     * 清除快速捕捉追踪线，释放几何体。
     */
    private clearQuickSnapLines(): void {
        if (this.quickSnapHorzLine) {
            this.disposePointHvTrackingLine(this.quickSnapHorzLine);
            this.quickSnapHorzLine = null;
        }
        if (this.quickSnapVertLine) {
            this.disposePointHvTrackingLine(this.quickSnapVertLine);
            this.quickSnapVertLine = null;
        }
    }

    /**
     * 清除所有快速捕捉状态（高亮点 + 追踪线）。
     */
    private clearQuickSnapState(): void {
        this.clearQuickSnapHighlight();
        this.clearQuickSnapLines();
    }

    

    
    /**
     * 收集与点水平/竖直捕捉可使用的候选点。
     * 说明：
     * 1. 正式草图点来自活跃草图组，保持既有捕捉语义；
     * 2. 原点投影、悬停可见模型边后动态生成的端点/中点来自 `transientPointObjects`，只作为参考点参与 H/V 追踪；
     * 3. 使用 Set 去重，避免同一个点同时出现在正式集合和临时白名单时重复参与排序。
     * @param request 点捕捉请求参数。
     * @returns 已去重的候选点列表。
     */
    private collectCandidatePoints(request: PointSnapRequest): SketchPointObject[] {
        const candidatePointSet = new Set<SketchPointObject>();
        const activeSketchGroup = this.viewer?.sketchGroupManager?.activeSketchGroup ?? null;

        if (activeSketchGroup) {
            activeSketchGroup.getSketchPointObjects().forEach((point) => {
                if (this.isSnapTargetExcluded(request, point)) {
                    return;
                }
                candidatePointSet.add(point);
            });
        }

        request.transientPointObjects?.forEach((point) => {
            if (this.isSnapTargetExcluded(request, point)) {
                return;
            }
            candidatePointSet.add(point);
        });

        return Array.from(candidatePointSet);
    }

    /**
     * 将最佳模型候选与既有草图点按相同轴向规则比较；完全重合时保留正式草图点优先级。
     * @param current 既有草图候选。
     * @param candidate 索引返回的最佳模型候选。
     * @param request 当前指针请求。
     * @param referencePlane 草图参考面。
     * @param direction 当前追踪方向。
     * @returns 当前方向最终参考点。
     */
    private selectModelAxisCandidate(current: PointHorizontalVerticalCandidateInfo | null, candidate: ModelPointAxisCandidate | null,
        request: PointSnapRequest, referencePlane: ReferencePlane, direction: PointHorizontalVerticalTrackingLineType): PointHorizontalVerticalCandidateInfo | null {
        if (!candidate) return current;
        const screenAbsDx = Math.abs(candidate.screenPosition.x - request.screenPosition.x);
        const screenAbsDy = Math.abs(candidate.screenPosition.y - request.screenPosition.y);
        const horizontal = direction === 'horizontal';
        const error = horizontal ? screenAbsDy : screenAbsDx;
        const distance = horizontal ? screenAbsDx : screenAbsDy;
        if (current) {
            const currentError = horizontal ? current.screenAbsDy : current.screenAbsDx;
            const currentDistance = horizontal ? current.screenAbsDx : current.screenAbsDy;
            if (currentError < error || (currentError === error && currentDistance <= distance)) return current;
        }
        const worldPos = candidate.referencePoint.projectedPosition;
        const info = this.modelAxisCandidateInfos[horizontal ? 0 : 1]!;
        info.worldPos.copy(worldPos);
        referencePlane.worldToLocalTo(worldPos, info.localPos);
        info.screenAbsDx = screenAbsDx;
        info.screenAbsDy = screenAbsDy;
        info.modelReference = candidate;
        return info;
    }
    

    /**
     * 更新与点水平/竖直追踪虚线（普通模式）。
     *
     * 说明：
     * 1. 虚线从参考点连接到鼠标在对应轴上的投影点；
     * 2. 已存在追踪线时只原地更新几何，避免鼠标移动高频创建对象；
     * 3. 首次命中对应方向时才创建线对象并加入场景。
     *
     * @param type 对齐类型：水平或竖直。
     * @param start 线段起点（参考草图点的世界坐标）。
     * @param end 原始线段终点（鼠标投影点的世界坐标，显示时会被限制到统一长度）。
     */
    private updatePointHvTrackingLine(
        type: PointHorizontalVerticalTrackingLineType,
        start: THREE.Vector3,
        end: THREE.Vector3,
    ): void {
        const guideEnd = this.resolveLimitedPointSnapGuideLineEndPoint(start, end);
        const existingLine = this.pointHvTrackingLines[type];
        if (existingLine) {
            existingLine.visible = true;
            this.updatePointHvTrackingLineGeometry(existingLine, [start, guideEnd]);
            return;
        }

        const line = this.createPointHvTrackingLine(start, guideEnd);
        this.addPreviewObject(line);
        this.pointHvTrackingLines[type] = line;
    }

    /**
     * 隐藏本帧未命中的普通模式与点 H/V 追踪线。
     *
     * 说明：隐藏而不是释放，保证下次同方向命中可以直接复用对象。
     *
     * @param horizontalActive 本帧水平追踪是否命中。
     * @param verticalActive 本帧竖直追踪是否命中。
     */
    private hideInactivePointHvTrackingLines(horizontalActive: boolean, verticalActive: boolean): void {
        const horizontalLine = this.pointHvTrackingLines[PointHorizontalVerticalTrackingLineType.Horizontal];
        if (horizontalLine && !horizontalActive) {
            horizontalLine.visible = false;
        }

        const verticalLine = this.pointHvTrackingLines[PointHorizontalVerticalTrackingLineType.Vertical];
        if (verticalLine && !verticalActive) {
            verticalLine.visible = false;
        }
    }
    

    
    /**
     * 创建与点水平/竖直追踪线。
     *
     * 设计说明：
     * 1. 追踪线只负责显示捕捉参考，不表达真实草图实体；
     * 2. 材质必须来自 `material` 目录的屏幕空间虚线方案，保证缩放时虚线节奏稳定；
     * 3. 创建后立即同步几何距离 attribute，避免渲染阶段拿不到虚线百分比数据。
     *
     * @param start 追踪线起点。
     * @param end 已限制长度后的追踪线终点。
     * @returns 已同步屏幕空间虚线数据的追踪线对象。
     */
    private createPointHvTrackingLine(start: THREE.Vector3, end: THREE.Vector3): THREE.Line {
        const points = [start, end];
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const line = new THREE.Line(
            geometry,
            LineMaterialFactory.createPointHvScreenSpacePreviewMaterial(points),
        );
        syncScreenSpaceDashedLineGeometry(line);
        line.renderOrder = 999;
        return line;
    }

    /**
     * 原地更新与点水平/竖直追踪线几何。
     *
     * 设计说明：
     * 1. 与圆预览线保持同一更新策略：顶点数量一致时只更新 position attribute；
     * 2. 顶点数量变化时才让 BufferGeometry 重建点列；
     * 3. 每次更新后同步屏幕空间虚线属性，保证材质 uniform 与几何一致。
     *
     * @param line 待更新的追踪线。
     * @param points 新点列。
     */
    private updatePointHvTrackingLineGeometry(line: THREE.Line, points: THREE.Vector3[]): void {
        const positionAttribute = line.geometry.getAttribute('position');
        if (positionAttribute instanceof THREE.BufferAttribute && positionAttribute.count === points.length) {
            points.forEach((point, index) => {
                positionAttribute.setXYZ(index, point.x, point.y, point.z);
            });
            positionAttribute.needsUpdate = true;
        } else {
            line.geometry.setFromPoints(points);
        }

        line.geometry.computeBoundingSphere();
        syncScreenSpaceDashedLineGeometry(line);
    }

    /**
     * 释放与点水平/竖直追踪线。
     *
     * 设计说明：
     * 1. 屏幕空间虚线材质每条线独享实例，不能只释放几何；
     * 2. 释放仍延后到当前渲染帧之后，避免渲染器访问刚移除的 GPU 资源。
     *
     * @param line 待释放的追踪线。
     */
    private disposePointHvTrackingLine(line: THREE.Line): void {
        this.scene?.remove(line);
        scheduleDisposableResourceDisposal(line.geometry);

        const material = Array.isArray(line.material)
            ? line.material[0]
            : line.material;
        if (isScreenSpaceDashedLineNodeMaterial(material)) {
            scheduleDisposableResourceDisposal(material);
        }
    }

    /**
     * 释放普通模式与点水平/竖直追踪线。
     *
     * 说明：仅在策略清理、工具切换或绘制结束时调用，鼠标移动过程不走该路径。
     */
    private disposePointHvTrackingLines(): void {
        const horizontalLine = this.pointHvTrackingLines[PointHorizontalVerticalTrackingLineType.Horizontal];
        if (horizontalLine) {
            this.disposePointHvTrackingLine(horizontalLine);
            this.pointHvTrackingLines[PointHorizontalVerticalTrackingLineType.Horizontal] = null;
        }

        const verticalLine = this.pointHvTrackingLines[PointHorizontalVerticalTrackingLineType.Vertical];
        if (verticalLine) {
            this.disposePointHvTrackingLine(verticalLine);
            this.pointHvTrackingLines[PointHorizontalVerticalTrackingLineType.Vertical] = null;
        }
    }
    
}



